/**
 * Presupuestos.
 *
 * La pantalla está construida alrededor de una idea: **no basta con saber cuánto
 * llevas gastado, hay que saber si vas a tiempo**. Por eso cada presupuesto muestra
 * el ritmo («a este paso lo agotas en 6 días»), que es lo que de verdad permite
 * reaccionar, en lugar de un número que ya no tiene arreglo.
 *
 * El arrastre del sobrante se explica en pantalla, porque es una función que
 * mucha gente no sabe que existe y que en Cuentia se podía activar sin que hiciera
 * nada.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Copy, Lightbulb, Plus, Trash2, TriangleAlert } from 'lucide-react';
import { useState } from 'react';

import { addMonths, currentMonthKey, formatCOP, formatMonthLabel } from '@eldescuadre/core';

import {
  deleteBudget,
  getBudgetSuggestions,
  getBudgets,
  getReportBudgets,
  saveBudget,
} from '../lib/endpoints';
import { ApiError } from '../lib/api';
import { Card, EmptyState, ErrorNote, Field, Loading, Modal, Money, Note, Progress, SubmitButton } from '../components/ui';

export function BudgetsPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const [monthKey, setMonthKey] = useState(currentMonthKey());
  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [suggesting, setSuggesting] = useState(false);

  const pacing = useQuery({
    queryKey: ['budgets', 'pacing', monthKey],
    queryFn: () => getReportBudgets(monthKey),
  });
  const budgets = useQuery({
    queryKey: ['budgets', monthKey],
    queryFn: () => getBudgets(monthKey),
  });

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['budgets'] }),
      queryClient.invalidateQueries({ queryKey: ['reports'] }),
    ]);
  };

  const remove = useMutation({ mutationFn: deleteBudget, onSuccess: refresh });

  const copy = useMutation({
    mutationFn: async () => {
      const { request } = await import('../lib/api');
      return request('/api/budgets/copy', {
        method: 'POST',
        body: { from: addMonths(monthKey, -1), to: monthKey },
      });
    },
    onSuccess: refresh,
  });

  const totalBudget = (pacing.data?.budgets ?? []).reduce((acc, row) => acc + row.available, 0);
  const totalSpent = (pacing.data?.budgets ?? []).reduce((acc, row) => acc + row.spent, 0);
  const isCurrentMonth = monthKey === currentMonthKey();

  return (
    <div className="space-y-4 animate-ed-fade-in">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight">Presupuestos</h1>
          <p className="mt-0.5 text-sm text-ed-text-muted">
            Cuánto pensabas gastar y cómo vas de verdad.
          </p>
        </div>
        <button
          type="button"
          className="ed-btn-primary !min-h-[44px] !w-auto shrink-0 !px-4"
          onClick={() => setCreating(true)}
        >
          <Plus size={18} aria-hidden="true" />
          Añadir
        </button>
      </header>

      {/* Cambio de mes */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="ed-btn-ghost !w-auto !px-3"
          onClick={() => setMonthKey((m) => addMonths(m, -1))}
          aria-label="Mes anterior"
        >
          <ChevronLeft size={18} />
        </button>
        <span className="flex-1 text-center text-sm font-medium capitalize">
          {formatMonthLabel(monthKey)}
        </span>
        <button
          type="button"
          className="ed-btn-ghost !w-auto !px-3"
          onClick={() => setMonthKey((m) => addMonths(m, 1))}
          aria-label="Mes siguiente"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {pacing.isLoading && <Loading label="Calculando tus presupuestos…" />}
      {copy.isError && (
        <ErrorNote
          message="No se pudieron copiar los presupuestos del mes pasado."
          hint="Comprueba que el mes anterior tenga presupuestos guardados."
        />
      )}

      {pacing.data && pacing.data.budgets.length === 0 && (
        <Card>
          <EmptyState
            title="Sin presupuestos este mes"
            message="Un presupuesto es el límite que te pones en una categoría. Con dos o tres ya se nota."
            action={
              <div className="flex flex-wrap justify-center gap-2">
                <button
                  type="button"
                  className="ed-btn-primary !w-auto !px-4"
                  onClick={() => setCreating(true)}
                >
                  Crear el primero
                </button>
                <button
                  type="button"
                  className="ed-btn-secondary !w-auto !px-4"
                  onClick={() => setSuggesting(true)}
                >
                  <Lightbulb size={16} aria-hidden="true" />
                  Sugiéreme montos
                </button>
              </div>
            }
          />
        </Card>
      )}

      {pacing.data && pacing.data.budgets.length > 0 && (
        <>
          {/* Resumen general del mes */}
          <Card>
            <div className="mb-2 flex items-baseline justify-between gap-3">
              <span className="text-sm text-ed-text-muted">Total presupuestado</span>
              <span className="flex items-baseline gap-2">
                <Money amount={totalSpent} className="font-display text-lg font-bold" />
                <span className="text-sm text-ed-text-muted">de</span>
                <Money amount={totalBudget} className="text-sm" />
              </span>
            </div>
            <Progress
              percent={totalBudget > 0 ? (totalSpent / totalBudget) * 100 : 0}
              tone={totalSpent > totalBudget ? 'coral' : 'gold'}
              label="Consumo total del presupuesto"
            />
            <p className="mt-2 text-xs text-ed-text-muted">
              {totalSpent > totalBudget ? (
                <span className="text-ed-coral-light">
                  Te pasaste por <Money amount={totalSpent - totalBudget} />.
                </span>
              ) : (
                <>
                  Te queda <Money amount={totalBudget - totalSpent} /> para lo que resta de mes.
                </>
              )}
            </p>
          </Card>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="ed-btn-secondary !w-auto !px-4 text-xs"
              disabled={copy.isPending}
              onClick={() => copy.mutate()}
            >
              <Copy size={14} aria-hidden="true" />
              Copiar los del mes pasado
            </button>
            <button
              type="button"
              className="ed-btn-ghost !w-auto !px-4 text-xs"
              onClick={() => setSuggesting(true)}
            >
              <Lightbulb size={14} aria-hidden="true" />
              Sugiéreme montos
            </button>
          </div>

          {/* Uno por categoría, con su ritmo */}
          <ul className="space-y-3">
            {pacing.data.budgets.map((budget) => {
              const isOver = budget.spent > budget.available;
              const isLate = budget.onTrackToExceed && !isOver;

              return (
                <li key={budget.categoryId}>
                  <Card className="!p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate font-medium">{budget.name}</p>
                        <p className="mt-0.5 text-xs text-ed-text-muted">
                          <Money amount={budget.spent} /> de <Money amount={budget.available} />
                        </p>
                      </div>

                      <div className="flex shrink-0 items-center gap-1">
                        <button
                          type="button"
                          className="ed-btn-ghost !min-h-[36px] !px-2 text-xs"
                          onClick={() => setEditing(budget.categoryId)}
                          aria-label={`Editar el presupuesto de ${budget.name}`}
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          className="ed-btn-ghost !min-h-[36px] !px-2 text-ed-coral-light"
                          onClick={() => {
                            const found = (budgets.data?.budgets ?? []).find(
                              (b) => b.categoryId === budget.categoryId,
                            );
                            if (found && window.confirm(`¿Quitar el presupuesto de ${budget.name}?`)) {
                              remove.mutate(found.id);
                            }
                          }}
                          aria-label={`Quitar el presupuesto de ${budget.name}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>

                    <div className="mt-2">
                      <Progress
                        percent={budget.percentUsed}
                        tone={isOver ? 'coral' : isLate ? 'gold' : 'success'}
                        label={`${budget.name}: ${Math.round(budget.percentUsed)} por ciento usado`}
                      />
                    </div>

                    <p className="mt-2 text-xs">
                      {isOver ? (
                        <span className="text-ed-coral-light">
                          Te pasaste por <Money amount={budget.spent - budget.available} />. El
                          exceso se descuenta del mes que viene si tienes el arrastre activado.
                        </span>
                      ) : isLate ? (
                        <span className="flex items-center gap-1.5 text-ed-gold">
                          <TriangleAlert size={13} aria-hidden="true" />
                          A este paso lo agotas en {budget.daysUntilExhausted}{' '}
                          {budget.daysUntilExhausted === 1 ? 'día' : 'días'}, y al mes le quedan más.
                        </span>
                      ) : (
                        <span className="text-ed-success">
                          Vas bien. Te queda <Money amount={budget.remaining} />.
                        </span>
                      )}
                    </p>
                  </Card>
                </li>
              );
            })}
          </ul>

          {isCurrentMonth && (
            <Note>
              El <strong>ritmo</strong> se calcula con los días transcurridos del mes en hora de
              Colombia, así que no se adelanta por la noche ni se desmadra a fin de mes.
            </Note>
          )}
        </>
      )}

      {(creating || editing) && (
        <BudgetModal
          monthKey={monthKey}
          categoryId={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={async () => {
            setCreating(false);
            setEditing(null);
            await refresh();
          }}
        />
      )}

      {suggesting && (
        <SuggestionsModal
          monthKey={monthKey}
          onClose={() => setSuggesting(false)}
          onSaved={async () => {
            setSuggesting(false);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

/** Crear o editar el presupuesto de una categoría. */
function BudgetModal({
  monthKey,
  categoryId,
  onClose,
  onSaved,
}: {
  monthKey: string;
  categoryId: string | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}): React.JSX.Element {
  const [chosenCategory, setChosenCategory] = useState(categoryId ?? '');
  const [amountText, setAmountText] = useState('');
  const [rollover, setRollover] = useState(false);

  const categories = useQuery({
    queryKey: ['categories', 'EXPENSE'],
    queryFn: () => import('../lib/endpoints').then((m) => m.getCategories('EXPENSE')),
  });
  const budgets = useQuery({
    queryKey: ['budgets', monthKey],
    queryFn: () => getBudgets(monthKey),
  });

  // Al editar, se rellenan los valores actuales.
  const existing = (budgets.data?.budgets ?? []).find((b) => b.categoryId === chosenCategory);

  const amount = Number(amountText.replace(/\./g, '').replace(',', '.')) || 0;

  const save = useMutation({
    mutationFn: () =>
      saveBudget({
        categoryId: chosenCategory,
        monthKey,
        amount,
        rollover,
      }),
    onSuccess: onSaved,
  });

  const error = save.error instanceof ApiError ? save.error : null;

  return (
    <Modal
      open
      onClose={onClose}
      title={categoryId ? 'Editar presupuesto' : 'Nuevo presupuesto'}
      footer={
        <SubmitButton
          type="button"
          pending={save.isPending}
          disabled={chosenCategory === '' || amount <= 0}
          onClick={() => save.mutate()}
        >
          Guardar
        </SubmitButton>
      }
    >
      <div className="space-y-4">
        <Field label="Categoría" htmlFor="categoria-presupuesto">
          <select
            id="categoria-presupuesto"
            className="ed-input"
            value={chosenCategory}
            onChange={(e) => {
              setChosenCategory(e.target.value);
              const found = (budgets.data?.budgets ?? []).find(
                (b) => b.categoryId === e.target.value,
              );
              setAmountText(found ? String(found.amount) : '');
              setRollover(found?.rollover ?? false);
            }}
            disabled={categoryId !== null}
          >
            <option value="">Elige una categoría…</option>
            {(categories.data?.categories ?? []).map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label={`Límite para ${formatMonthLabel(monthKey)}`}
          htmlFor="monto-presupuesto"
          hint="Escribe el monto en pesos."
        >
          <input
            id="monto-presupuesto"
            className="ed-input ed-money"
            inputMode="numeric"
            value={amountText}
            onChange={(e) => setAmountText(e.target.value)}
            placeholder="600.000"
            autoComplete="off"
          />
        </Field>

        <label className="flex items-start gap-3 rounded-ed border border-ed-bg-border/30 bg-ed-bg-elevated/40 p-3">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-ed-bg-border"
            checked={rollover}
            onChange={(e) => setRollover(e.target.checked)}
          />
          <span className="text-sm">
            <span className="block font-medium">Arrastrar lo que sobre</span>
            <span className="mt-0.5 block text-xs text-ed-text-muted">
              Si este mes no gastas todo el presupuesto, lo que sobre se suma al mes siguiente. Si
              te pasas, el exceso <strong>no</strong> se arrastra: no se acumula deuda.
            </span>
          </span>
        </label>

        {existing && existing.rolledOver > 0 && (
          <Note>
            Este mes arrastras <Money amount={existing.rolledOver} className="font-semibold" /> del
            mes pasado. Tu límite real es {formatCOP(existing.available)}.
          </Note>
        )}

        {error && <ErrorNote message={error.detailMessage} />}
      </div>
    </Modal>
  );
}

/** Sugerencias a partir de lo que de verdad gastaste. */
function SuggestionsModal({
  monthKey,
  onClose,
  onSaved,
}: {
  monthKey: string;
  onClose: () => void;
  onSaved: () => Promise<void>;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const [chosen, setChosen] = useState<Set<string>>(new Set());

  const suggestions = useQuery({
    queryKey: ['budgets', 'suggestions', monthKey],
    queryFn: () => getBudgetSuggestions(monthKey),
  });
  const categories = useQuery({
    queryKey: ['categories', 'EXPENSE'],
    queryFn: () => import('../lib/endpoints').then((m) => m.getCategories('EXPENSE')),
  });

  const names = new Map((categories.data?.categories ?? []).map((c) => [c.id, c.name]));

  const apply = useMutation({
    mutationFn: async () => {
      const rows = (suggestions.data?.suggestions ?? []).filter((s) => chosen.has(s.categoryId));
      for (const row of rows) {
        await saveBudget({
          categoryId: row.categoryId,
          monthKey,
          amount: row.suggestedAmount,
          rollover: false,
        });
      }
      return rows.length;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['budgets'] });
      await onSaved();
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Montos sugeridos"
      footer={
        <SubmitButton
          type="button"
          pending={apply.isPending}
          disabled={chosen.size === 0}
          onClick={() => apply.mutate()}
        >
          Usar {chosen.size > 0 ? chosen.size : ''}{' '}
          {chosen.size === 1 ? 'sugerencia' : 'sugerencias'}
        </SubmitButton>
      }
    >
      <div className="space-y-4">
        <Note>
          Calculados con lo que <strong>de verdad</strong> gastaste en los últimos tres meses, no
          con una cifra inventada. Empezar de cero con un número realista es mucho más fácil.
        </Note>

        {suggestions.isLoading && <Loading />}

        {suggestions.data && suggestions.data.suggestions.length === 0 && (
          <EmptyState
            title="Todavía no hay historial"
            message="Cuando lleves dos o tres meses registrando gastos, podremos proponerte límites realistas."
          />
        )}

        {suggestions.data && suggestions.data.suggestions.length > 0 && (
          <ul className="divide-y divide-ed-bg-border/25">
            {suggestions.data.suggestions.map((row) => (
              <li key={row.categoryId} className="flex items-center gap-3 py-2.5">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-ed-bg-border"
                  checked={chosen.has(row.categoryId)}
                  onChange={(e) => {
                    setChosen((current) => {
                      const next = new Set(current);
                      if (e.target.checked) next.add(row.categoryId);
                      else next.delete(row.categoryId);
                      return next;
                    });
                  }}
                  aria-label={`Usar la sugerencia de ${names.get(row.categoryId) ?? 'la categoría'}`}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm">
                    {names.get(row.categoryId) ?? 'Categoría'}
                  </span>
                  <span className="block text-xs text-ed-text-muted">
                    Basado en {row.basedOnMonths}{' '}
                    {row.basedOnMonths === 1 ? 'mes' : 'meses'} de gastos
                  </span>
                </span>
                <Money amount={row.suggestedAmount} className="shrink-0 text-sm font-semibold" />
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
